import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, ChevronUp, ChevronDown, Users, Eye } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import '../../components/admin/MobileCardList.css';

const CustomerManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);


  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCustomers();
      const data = response.data || response;
      const customersList = Array.isArray(data) ? data : (data.results || []);
      setCustomers(customersList);
    } catch (error) {
      console.error('Failed to load customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };

  const customersArray = Array.isArray(customers) ? customers : [];
  const filtered = useMemo(() => {
    let list = customersArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(c =>
        c.username?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [customersArray, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();
  const fmtDate = (v: string) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'â€”';

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Customer Management</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-refresh" onClick={loadCustomers} disabled={loading}>
            {loading ? <span className="adm-spin"><RefreshCw size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <span className="adm-count"><strong>{filtered.length}</strong> customers</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by name, email, phoneâ€¦" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading customersâ€¦</div>}
          {!loading && filtered.length === 0 && <div className="mc-empty">No customers found.</div>}
          {!loading && filtered.map((c: any) => (
            <div className="mobile-card" key={c.id}>
              <div className="mc-top">
                <div className="mc-thumb-placeholder"><Users size={22} /></div>
                <div className="mc-info">
                  <div className="mc-title">{c.username || c.email}</div>
                  <div className="mc-subtitle">{c.email}</div>
                </div>
              </div>
              <div className="mc-divider" />
              <div className="mc-meta">
                {c.phone && <div className="mc-meta-item"><span className="mc-meta-label">Phone:</span><span className="mc-meta-value">{c.phone}</span></div>}
                {c.city && <div className="mc-meta-item"><span className="mc-meta-label">City:</span><span className="mc-meta-value">{c.city}</span></div>}
                {c.created_at && <div className="mc-meta-item"><span className="mc-meta-label">Joined:</span><span className="mc-meta-value">{fmtDate(c.created_at)}</span></div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <div className="adm-table-card">
          <div className="adm-table-scroll">
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('username')}>Name <SA field="username" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('email')}>Email <SA field="email" /></th>
                  <th className="adm-th">Phone</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('city')}>City <SA field="city" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('created_at')}>Joined <SA field="created_at" /></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="adm-loading">Loading customersâ€¦</td></tr>}
                {!loading && paginated.length === 0 && <tr><td colSpan={5} className="adm-empty">No customers found.</td></tr>}
                {!loading && paginated.map((c: any) => (
                  <tr className="adm-row" key={c.id}>
                    <td className="adm-td"><span className="adm-name-main">{c.username || 'â€”'}</span></td>
                    <td className="adm-td"><span className="adm-text-muted" style={{ fontSize: 11 }}>{c.email}</span></td>
                    <td className="adm-td"><span className="adm-text-muted">{c.phone || 'â€”'}</span></td>
                    <td className="adm-td"><span className="adm-text-muted">{c.city || 'â€”'}</span></td>
                    <td className="adm-td"><span className="adm-date">{fmtDate(c.created_at)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="adm-pagination">
            <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50,100].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
            <div className="adm-page-info">{filtered.length === 0 ? '0 customers' : `${(currentPage-1)*pageSize+1}â€“${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
            <div className="adm-page-controls">
              <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>â€¹</button>
              <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>â€¦</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
              <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>â€º</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
